const { status } = require('./const');

class ScriptDefinition {
    static loadingScripts = new Map();
    static globalErrorListener = null;

    /**
     * Add script instance to the loading scripts map
     *
     * @param {ScriptDefinition} script
     */
    static addLoadingScript(script) {
        this.loadingScripts.set(script.url, script);
    }

    /**
     * Set global error listener
     */
    static setGlobalErrorListener() {
        if (this.globalErrorListener) {
            return;
        }

        this.globalErrorListener = (event) => {
            console.warn(`ScriptDefinition -> globalErrorListener: file: ${event.filename}, message: ${event.message}`);
            for (const [, instance] of ScriptDefinition.loadingScripts.entries()) {
                if (instance.isRelatedError(event.filename) && instance.isSyntaxError(event.message, event.error)) {
                    instance.handleSyntaxError(event.message);
                    break;
                }
            }
        };

        window.addEventListener('error', this.globalErrorListener);
    }

    /**
     * Remove global error listener
     */
    static removeGlobalErrorListener() {
        window.removeEventListener('error', this.globalErrorListener);
        this.globalErrorListener = null;
    }

    /**
     * Cleanup loading scripts map and global error listener
     */
    static cleanup() {
        this.loadingScripts.clear();
        this.removeGlobalErrorListener();
    }

    /**
     *
     * @param {string} name - friendly script name
     * @param {string} url - script's src
     * @param {object} [attributes] - additional attributes, for example {"type": "module", "crossorigin": true}; no need for `type="text/javascript"`, see https://stackoverflow.com/a/5265361/4536543
     */
    constructor(name, url, attributes) {
        this.name = name;
        this.url = url;
        this.attributes = { async: false, ...attributes }; // async=false to respect order, see https://webplatform.github.io/docs/html/attributes/async/#Programmatically-Created-Scripts
        this.isLoading = false;
    }

    onLoad() {
        this.resolveLoading(status.loaded);
    }

    onError(event) {
        console.warn(`ScriptDefinition -> onError: Script loading error in ${this.name} (${this.url}): ${event.message}`);
        this.rejectLoading(status.errored);
    }

    stripQueryAndHash(url) {
        return url ? url.split(/[?#]/)[0] : '';
    }

    isRelatedError(filename) {
        if (!filename) return false;
        const scriptFile = this.stripQueryAndHash(this.url).split('/').pop();
        const errorFile = this.stripQueryAndHash(filename).split('/').pop();
        return scriptFile === errorFile;
    }

    isSyntaxError(errorMessage, error) {
        return errorMessage.toLowerCase().includes('syntaxerror') || error?.name?.toLowerCase() === 'syntaxerror';
    }

    /**
     * @description Handles syntax errors during script loading
     * @param {string} errorMessage
     */
    handleSyntaxError(errorMessage) {
        console.error(`ScriptDefinition -> handleSyntaxError: Script loading error in ${this.name} (${this.url}): ${errorMessage}`);
        this.rejectLoading(status.errored);
    }

    status = new Promise((resolve, reject) => {
        this.resolveLoading = resolve;
        this.rejectLoading = reject;
    });

    /**
     * @returns {Promise<{name: string, url: string, attributes?: object, status: import("./const").ScriptLoadingStatus}>}
     */
    async serialize() {
        /** @type {import("./const").ScriptLoadingStatus} */
        let scriptStatus;
        try {
            scriptStatus = await Promise.race([this.status, Promise.resolve(status.unfinished)]); // if status promise is not already resolved/rejected - "unfinished" will be returned
        } catch (rejectionStatus) {
            scriptStatus = rejectionStatus;
        }
        return {
            name: this.name,
            url: this.url,
            attributes: this.attributes,
            status: scriptStatus,
        };
    }

    /**
     * @returns {HTMLScriptElement}
     */
    render() {
        const element = document.createElement('script');

        this.isLoading = true;

        ScriptDefinition.addLoadingScript(this);
        ScriptDefinition.setGlobalErrorListener();

        element.onload = () => this.onLoad();
        element.onerror = (event) => this.onError(event);

        // Add cache busting if enabled (for hard reload scenarios)
        let scriptUrl = this.url;
        const cacheBustTimestamp = sessionStorage.getItem('CACHE_BUST_TIMESTAMP');
        if (cacheBustTimestamp) {
            const separator = scriptUrl.includes('?') ? '&' : '?';
            scriptUrl = scriptUrl + separator + '_cb=' + cacheBustTimestamp;
        }

        element.src = scriptUrl;

        Object.keys(this.attributes).forEach((attributeName) => {
            element[attributeName] = this.attributes[attributeName];
        });

        return element;
    }
}

module.exports = ScriptDefinition;
